import { create } from 'zustand';
import {
  GetOnboardingProgressUseCase,
  StartOnboardingUseCase,
  CompleteOnboardingStepUseCase,
  SkipOnboardingUseCase,
  ResetOnboardingUseCase,
} from '@/features/onboarding/application/use-cases';
import { SupabaseOnboardingRepository } from '@/features/onboarding/infrastructure/repositories/SupabaseOnboardingRepository';
import { OnboardingProgressDTO } from '@/features/onboarding/application/dto/OnboardingProgressDTO';

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  targetSelector?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

interface OnboardingState {
  // Current onboarding progress from backend
  progress: OnboardingProgressDTO | null;

  // Local UI state
  isActive: boolean;
  currentStepIndex: number;
  currentRole: string | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  loadProgress: (userId: string, role: string) => Promise<void>;
  startOnboarding: (userId: string, role: string) => Promise<void>;
  nextStep: (userId: string, role: string, stepId: string) => Promise<void>;
  previousStep: () => void;
  skipOnboarding: (userId: string, role: string) => Promise<void>;
  completeOnboarding: (userId: string, role: string) => Promise<void>;
  resetOnboarding: (userId: string, role: string) => Promise<void>;
  setCurrentStep: (index: number) => void;

  // UI control
  hideOnboarding: () => void;
  showOnboarding: () => void;
}

// Singleton repository and use cases
const repository = new SupabaseOnboardingRepository();
const getProgressUseCase = new GetOnboardingProgressUseCase(repository);
const startUseCase = new StartOnboardingUseCase(repository);
const completeStepUseCase = new CompleteOnboardingStepUseCase(repository);
const skipUseCase = new SkipOnboardingUseCase(repository);
const resetUseCase = new ResetOnboardingUseCase(repository);

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
  progress: null,
  isActive: false,
  currentStepIndex: 0,
  currentRole: null,
  isLoading: false,
  error: null,

  /**
   * Load onboarding progress from backend
   */
  loadProgress: async (userId: string, role: string) => {
    try {
      set({ isLoading: true, error: null });
      const progress = await getProgressUseCase.execute(userId, role);

      set({
        progress,
        currentStepIndex: progress.currentStepIndex,
        currentRole: role,
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to load onboarding progress:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to load progress',
        isLoading: false,
      });
    }
  },

  /**
   * Start the onboarding process
   */
  startOnboarding: async (userId: string, role: string) => {
    try {
      set({ isLoading: true, error: null });
      const progress = await startUseCase.execute(userId, role);

      set({
        progress,
        isActive: true,
        currentStepIndex: progress.currentStepIndex,
        currentRole: role,
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to start onboarding:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to start onboarding',
        isLoading: false,
      });
    }
  },

  /**
   * Move to next step
   */
  nextStep: async (userId: string, role: string, stepId: string) => {
    try {
      const currentIndex = get().currentStepIndex;
      const nextIndex = currentIndex + 1;

      set({ isLoading: true, error: null });

      const progress = await completeStepUseCase.execute({
        userId,
        role,
        stepId,
        nextStepIndex: nextIndex,
      });

      set({
        progress,
        currentStepIndex: progress.currentStepIndex,
        isLoading: false,
        // Auto-close if completed
        isActive: !progress.hasFinished,
      });
    } catch (error) {
      console.error('Failed to complete step:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to complete step',
        isLoading: false,
      });
    }
  },

  /**
   * Move to previous step (local only)
   */
  previousStep: () => {
    set((state) => ({
      currentStepIndex: Math.max(0, state.currentStepIndex - 1),
    }));
  },

  /**
   * Skip the entire onboarding
   */
  skipOnboarding: async (userId: string, role: string) => {
    try {
      set({ isLoading: true, error: null });
      const progress = await skipUseCase.execute(userId, role);

      set({
        progress,
        isActive: false,
        currentStepIndex: 0,
        currentRole: null,
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to skip onboarding:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to skip onboarding',
        isLoading: false,
      });
    }
  },

  /**
   * Complete the onboarding manually
   */
  completeOnboarding: async (userId: string, role: string) => {
    try {
      set({ isLoading: true, error: null });

      // Use the repository directly to complete onboarding
      await repository.completeOnboarding(userId, role);
      const progressDTO = await getProgressUseCase.execute(userId, role);

      set({
        progress: progressDTO,
        isActive: false,
        currentStepIndex: 0,
        currentRole: null,
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to complete onboarding',
        isLoading: false,
      });
    }
  },

  /**
   * Reset onboarding to initial state
   */
  resetOnboarding: async (userId: string, role: string) => {
    try {
      set({ isLoading: true, error: null });
      const progress = await resetUseCase.execute(userId, role);

      set({
        progress,
        isActive: false,
        currentStepIndex: 0,
        currentRole: role,
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to reset onboarding:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to reset onboarding',
        isLoading: false,
      });
    }
  },

  /**
   * Set current step manually (for navigation)
   */
  setCurrentStep: (index: number) => {
    set({ currentStepIndex: index });
  },

  /**
   * Hide onboarding UI (local only)
   */
  hideOnboarding: () => {
    set({ isActive: false });
  },

  /**
   * Show onboarding UI (local only)
   */
  showOnboarding: () => {
    set({ isActive: true });
  },
}));

// Onboarding steps by role - Prescriptive, action-oriented language
// Selectors use data-onboarding attributes set by Sidebar component
export const onboardingSteps: Record<string, OnboardingStep[]> = {
  director: [
    {
      id: 'dashboard-stats',
      title: 'Controla tu institución de un vistazo',
      description: 'Este panel te muestra si tu institución está creciendo o si hay problemas. Estudiantes activos, profesores disponibles, y rendimiento promedio — todo en tiempo real para tomar decisiones informadas.',
      targetSelector: '#dashboard-stats-section, [data-onboarding="dashboard-stats"]',
      position: 'bottom',
    },
    {
      id: 'academic-structure',
      title: 'Construye los cimientos académicos',
      description: 'Sin niveles y materias, tu plataforma está vacía. Aquí defines la jerarquía: crea niveles (ej: "1° año") y materias (ej: "Matemáticas"). Esto desbloquea inscripciones y cursos.',
      targetSelector: '[data-onboarding="sidebar-estructura-link"], #sidebar-estructura-link, a[href="/estructura"]',
      position: 'right',
    },
    {
      id: 'assign-teachers',
      title: 'Activa cursos asignando profesores',
      description: 'Dentro de Estructura Académica, selecciona un nivel para ver sus materias. Las materias sin profesor muestran un botón naranja "Asignar Profesor" — haz clic para activarlas y permitir inscripciones.',
      targetSelector: '[data-onboarding="sidebar-estructura-link"], a[href="/estructura"]',
      position: 'right',
    },
    {
      id: 'notifications',
      title: 'Comunica anuncios importantes',
      description: 'Envía avisos de cambios de horario, eventos o recordatorios. Puedes notificar a toda la institución, un nivel específico, un curso o usuarios individuales.',
      targetSelector: '[data-onboarding="sidebar-notificaciones-link"], #sidebar-notificaciones-link, a[href="/notificaciones"]',
      position: 'right',
    },
    {
      id: 'statistics',
      title: 'Detecta problemas con estadísticas',
      description: 'Identifica estudiantes en riesgo, materias con bajo rendimiento, o profesores destacados. Los gráficos revelan tendencias que no son obvias en los datos crudos.',
      targetSelector: '[data-onboarding="sidebar-estadisticas-link"], #sidebar-estadisticas-link, a[href="/estadisticas"]',
      position: 'right',
    },
  ],
  teacher: [
    {
      id: 'dashboard-courses',
      title: 'Accede rápido a tus cursos',
      description: 'Todo lo que necesitas: cuántos estudiantes tienes, evaluaciones pendientes de calificar, y atajos para gestionar contenido. Haz clic en cualquier curso para entrar.',
      targetSelector: '#dashboard-my-courses-section, [data-onboarding="dashboard-teacher-stats"]',
      position: 'bottom',
    },
    {
      id: 'my-subjects',
      title: 'Gestiona estudiantes y contenido',
      description: 'Aquí ves todas tus materias con la lista de estudiantes inscritos. Útil para verificar asistencia, enviar mensajes grupales, o descargar reportes.',
      targetSelector: '[data-onboarding="sidebar-mis-materias-link"], #sidebar-mis-materias-link, a[href="/mis-materias"]',
      position: 'right',
    },
    {
      id: 'lms',
      title: 'Crea lecciones y evaluaciones',
      description: 'Diseña tu curso: sube videos, crea quizzes con calificación automática, y asigna ensayos. Los estudiantes acceden a todo desde su LMS.',
      targetSelector: '[data-onboarding="sidebar-lms-link"], #sidebar-lms-link, a[href="/lms"]',
      position: 'right',
    },
    {
      id: 'grading',
      title: 'Califica y da retroalimentación',
      description: 'Los quizzes se califican solos — tú solo calificas ensayos. Revisa trabajos pendientes, asigna notas, y deja comentarios que el estudiante verá.',
      targetSelector: '[data-onboarding="sidebar-calificaciones-link"], #sidebar-calificaciones-link, a[href="/calificaciones"]',
      position: 'right',
    },
    {
      id: 'schedule',
      title: 'Verifica tu horario semanal',
      description: 'Consulta cuándo y dónde tienes clases esta semana. Evita conflictos y planifica tu tiempo. Los cambios del director aparecen automáticamente.',
      targetSelector: '[data-onboarding="sidebar-horarios-link"], #sidebar-horarios-link, a[href="/horarios"]',
      position: 'right',
    },
  ],
  student: [
    {
      id: 'dashboard-progress',
      title: 'Sigue tu avance en cada materia',
      description: 'Las barras de progreso muestran qué tan cerca estás de aprobar. También ves tareas pendientes y fechas límite — así nunca te atrasas.',
      targetSelector: '#dashboard-my-progress-section, [data-onboarding="dashboard-progress"]',
      position: 'bottom',
    },
    {
      id: 'enrollments',
      title: 'Inscríbete en materias disponibles',
      description: 'Solo puedes inscribirte si aprobaste las materias previas (mínimo 70%). Haz clic en "Inscribirse" en cualquier materia desbloqueada para comenzar.',
      targetSelector: '[data-onboarding="sidebar-inscripciones-link"], #sidebar-inscripciones-link, a[href="/inscripciones"]',
      position: 'right',
    },
    {
      id: 'lms',
      title: 'Accede a clases y tareas',
      description: 'Aquí estudias: ve videos, completa quizzes (se califican al instante), y entrega ensayos. Tu progreso se actualiza automáticamente.',
      targetSelector: '[data-onboarding="sidebar-lms-link"], #sidebar-lms-link, a[href="/lms"]',
      position: 'right',
    },
    {
      id: 'forum',
      title: 'Participa en el foro académico',
      description: 'Haz preguntas, ayuda a compañeros, y discute con profesores. Los posts con más likes aparecen primero — busca respuestas antes de preguntar.',
      targetSelector: '[data-onboarding="sidebar-forum-link"], #sidebar-forum-link, a[href="/forum"]',
      position: 'right',
    },
  ],
};
